/**
 * AINudge — Phase 36 Batch AA.3
 *
 * Step-6 "winner detector" for flip and BRRRR deals. Computes projected
 * margin from the live wizard form values and, when the math looks
 * compelling, surfaces an indigo CTA right above the submit button
 * urging the user to fire the AI breakdown.
 *
 * Why surface it here (and not on the AI tab post-save) ?
 *   - The user has already typed every input by the time they hit
 *     Step 6 — we have full ARV / repair / holding context for free.
 *   - The "Analyze deal" submit button already kicks off the same AI
 *     pipeline; this card is a confidence-boosting nudge that converts
 *     hesitant users who would otherwise bail at the final step.
 *
 * Visibility rules (per the brief):
 *   - flip / brrrr only — rentals + multifamily never see it.
 *   - flip   : margin = (ARV × (1 − selling%/100) − total_in) / total_in
 *              where total_in = purchase + repair + holding × period
 *                                + closing
 *   - brrrr  : margin = (ARV − total_in) / total_in (no selling cost,
 *              because BRRRR refinances rather than selling)
 *   - >= 65% → indigo "winner" card
 *   - < 10%  → amber "margin tight — review" warning (lower priority,
 *              still helpful so the user re-checks comps)
 *   - 10–64% → no nudge at all (neutral middle band)
 *
 * Tracking:
 *   - posthog ai_nudge_shown   { variant, margin_pct, deal_type, edit_mode }
 *   - posthog ai_nudge_clicked { variant, margin_pct, deal_type, edit_mode }
 *   - posthog ai_nudge_dismissed { variant, margin_pct, deal_type, edit_mode }
 *
 *   posthog is loaded via the snippet in public/index.html; if it isn't
 *   present (DNT, blocked, etc.) the optional chain silently no-ops.
 *
 * Dismissal:
 *   - sessionStorage key `wizard_ai_nudge_dismissed_{editId || "new"}`.
 *     Session-scoped so the nudge re-appears on the user's next visit
 *     instead of being silently muted forever.
 */
import { useEffect, useMemo, useState } from "react";
import { Sparkles, AlertTriangle, ArrowRight, Loader2 } from "lucide-react";

const WINNER_THRESHOLD = 65;
const WARNING_THRESHOLD = 10;

function asNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Pure helper exported for unit-style validation in screenshots. */
export function computeFlipMargin(a, purchasePrice) {
  const arv = asNum(a.arv);
  const repair = asNum(a.repair_budget);
  const holdingMo = asNum(a.holding_period_months);
  const holdingCost = asNum(a.holding_costs_monthly);
  const closing = asNum(a.closing_costs);
  const sellingPct = asNum(a.selling_costs_pct);

  const totalIn = asNum(purchasePrice) + repair + holdingMo * holdingCost + closing;
  const grossProceeds = arv * (1 - sellingPct / 100);
  if (totalIn <= 0 || arv <= 0) return null;
  const profit = grossProceeds - totalIn;
  return (profit / totalIn) * 100;
}

export function computeBrrrrMargin(a, purchasePrice) {
  const arv = asNum(a.arv);
  const repair = asNum(a.repair_budget);
  const stabMo = asNum(a.stabilization_months);
  const holdingCost = asNum(a.holding_costs_monthly);
  const closing = asNum(a.closing_costs);

  const totalIn = asNum(purchasePrice) + repair + stabMo * holdingCost + closing;
  if (totalIn <= 0 || arv <= 0) return null;
  const equity = arv - totalIn;
  return (equity / totalIn) * 100;
}

export default function AINudge({ wizard }) {
  const { dealType, form, submit, submitting, isEdit, editId } = wizard;

  const isFlip = dealType === "flip";
  const isBRRRR = dealType === "brrrr";
  const dismissKey = `wizard_ai_nudge_dismissed_${editId || "new"}`;

  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(dismissKey) === "1";
    } catch {
      return false;
    }
  });

  const margin = useMemo(() => {
    if (!isFlip && !isBRRRR) return null;
    const a = form.analysis || {};
    const purchase = asNum(form.property?.asking_price);
    return isFlip
      ? computeFlipMargin(a, purchase)
      : computeBrrrrMargin(a, purchase);
  }, [isFlip, isBRRRR, form]);

  // Decide which variant to render. `null` margin (zero inputs) hides
  // everything — there's nothing meaningful to nudge on.
  const variant =
    margin == null
      ? null
      : margin >= WINNER_THRESHOLD
      ? "winner"
      : margin < WARNING_THRESHOLD
      ? "warning"
      : null;

  const shouldRender = !dismissed && variant != null;

  // Fire `ai_nudge_shown` exactly once per (variant × mount). The
  // dependency on `variant` means a user who edits Step 4/5 and bumps
  // their numbers from 30% → 70% (winner) WILL trigger a second shown
  // event for the new variant — which is correct, the nudge surfaced
  // for a different reason.
  useEffect(() => {
    if (!shouldRender) return;
    try {
      window.posthog?.capture?.("ai_nudge_shown", {
        variant,
        margin_pct: Math.round(margin * 10) / 10,
        deal_type: dealType,
        edit_mode: !!isEdit,
      });
    } catch {
      /* posthog blocked / not loaded — ignore */
    }
  }, [shouldRender, variant, margin, dealType, isEdit]);

  if (!shouldRender) return null;

  const marginLabel = `${margin >= 0 ? "+" : ""}${margin.toFixed(1)}%`;

  const onDismiss = () => {
    try {
      sessionStorage.setItem(dismissKey, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
    try {
      window.posthog?.capture?.("ai_nudge_dismissed", {
        variant,
        margin_pct: Math.round(margin * 10) / 10,
        deal_type: dealType,
        edit_mode: !!isEdit,
      });
    } catch {
      /* ignore */
    }
  };

  const onCTA = () => {
    try {
      window.posthog?.capture?.("ai_nudge_clicked", {
        variant,
        margin_pct: Math.round(margin * 10) / 10,
        deal_type: dealType,
        edit_mode: !!isEdit,
      });
    } catch {
      /* ignore */
    }
    // Reuse the canonical submit handler — same path as the "Analyze
    // deal" button in DealWizard.jsx. Zero duplicate logic.
    submit();
  };

  if (variant === "winner") {
    return (
      <div
        data-testid="ai-nudge-winner"
        data-variant="winner"
        data-margin-pct={margin.toFixed(1)}
        className="relative overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-emerald-50 p-5 shadow-sm sm:p-6"
      >
        <div className="flex items-start gap-4">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-indigo-600 text-white shadow-md">
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold tracking-tight text-slate-900 sm:text-lg">
              This {isFlip ? "flip" : "BRRRR"} looks like a winner.
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Projected {isFlip ? "margin" : "instant equity"} of{" "}
              <span
                data-testid="ai-nudge-margin-value"
                className="font-semibold text-indigo-700"
              >
                {marginLabel}
              </span>
              . Want the full AI breakdown — risk score, comparable
              {isFlip ? " flips" : " refis"}, exit strategy, and offer
              ladder?
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onCTA}
                disabled={submitting}
                data-testid="ai-nudge-cta"
                className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-indigo-700 hover:shadow-md disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Running…
                  </>
                ) : (
                  <>
                    Run AI Breakdown
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={onDismiss}
                data-testid="ai-nudge-dismiss"
                className="text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // variant === "warning"
  return (
    <div
      data-testid="ai-nudge-warning"
      data-variant="warning"
      data-margin-pct={margin.toFixed(1)}
      className="relative overflow-hidden rounded-2xl border border-amber-200 bg-amber-50 p-5 shadow-sm sm:p-6"
    >
      <div className="flex items-start gap-4">
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-amber-500 text-white shadow-sm">
          <AlertTriangle className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold tracking-tight text-amber-900 sm:text-lg">
            Margin is tight — review before committing.
          </h3>
          <p className="mt-1 text-sm text-amber-900/90">
            At today’s inputs the projected{" "}
            {isFlip ? "flip margin" : "BRRRR equity"} is{" "}
            <span
              data-testid="ai-nudge-margin-value"
              className="font-semibold"
            >
              {marginLabel}
            </span>
            . Tighten ARV, drop the offer, or sharpen the rehab budget
            before you commit capital.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onCTA}
              disabled={submitting}
              data-testid="ai-nudge-cta"
              className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-amber-700 hover:shadow-md disabled:opacity-60 disabled:hover:translate-y-0"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running…
                </>
              ) : (
                <>
                  Run AI sanity check
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onDismiss}
              data-testid="ai-nudge-dismiss"
              className="text-sm font-medium text-amber-800/80 transition-colors hover:text-amber-900"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
